"use client";

import React, { createContext, useContext, useMemo, useState } from "react";

export type MobileTab = "input" | "output";

export const DEFAULT_ACTIVE_TAB: MobileTab = "input";

interface ActiveTabContextValue {
  activeTab: MobileTab;
  setActiveTab: (tab: MobileTab) => void;
}

const ActiveTabContext = createContext<ActiveTabContextValue | undefined>(undefined);

export const useActiveTab = (): ActiveTabContextValue => {
  const context = useContext(ActiveTabContext);
  if (context === undefined) {
    throw new Error("useActiveTab must be used within an ActiveTabProvider");
  }
  return context;
};

/**
 * Holds the mobile input/output tab selection. Scoped to the Compose surface
 * (the only place this two-pane layout exists) rather than living in a global
 * store — mounted by `ComposeSurface`, consumed by `MobileTabs` and the
 * user-prompt (which jumps to "output" after a run).
 */
export const ActiveTabProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTab, setActiveTab] = useState<MobileTab>(DEFAULT_ACTIVE_TAB);
  const value = useMemo(() => ({ activeTab, setActiveTab }), [activeTab]);
  return <ActiveTabContext.Provider value={value}>{children}</ActiveTabContext.Provider>;
};
